import type { NormalizedItem, TeachingEpisodeSource } from "./types";

const CONVERSATION_ID = /^conv_[A-Za-z0-9_-]+$/;
const MESSAGE_ID = /^msg_[A-Za-z0-9_-]+$/;
const SURFACE_ID = /^surface_[A-Za-z0-9_-]+$/;
const EPISODE_ID = /^teach_[A-Za-z0-9_-]+$/;
const EPISODE_STATES = new Set([
  "active", "summarizing", "awaiting_confirmation", "queued", "processing",
  "pr_open", "merged", "declined", "needs_attention", "cancelled", "expired",
]);

export interface TeachingEpisodeTableEntity {
  partitionKey: string;
  rowKey: string;
  episodeId?: unknown;
  kind?: unknown;
  payload?: unknown;
  state?: unknown;
  sourceConversationIds?: unknown;
}

export interface TeachingEpisodeTableSource {
  listEntities(options?: unknown): AsyncIterable<TeachingEpisodeTableEntity>;
}

interface TeachingSpanRecord {
  ordinal: number;
  conversationId: string;
  startAfterItemId?: string;
  endAtItemId?: string;
  empty?: true;
}

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalMessageId(value: unknown): value is string | undefined {
  return value === undefined || (typeof value === "string" && MESSAGE_ID.test(value));
}

function parseSpan(value: unknown, expectedOrdinal: number): TeachingSpanRecord {
  if (!object(value)) throw new Error("Invalid TeachingEpisodes row: malformed span");
  const { ordinal, conversationId, startAfterItemId, endAtItemId, empty } = value;
  if (
    ordinal !== expectedOrdinal
    || typeof conversationId !== "string"
    || !CONVERSATION_ID.test(conversationId)
    || !optionalMessageId(startAfterItemId)
    || !optionalMessageId(endAtItemId)
    || (empty !== undefined && empty !== true)
  ) {
    throw new Error("Invalid TeachingEpisodes row: malformed span");
  }
  return value as unknown as TeachingSpanRecord;
}

function parseEpisode(entity: TeachingEpisodeTableEntity): { spans: TeachingSpanRecord[] } {
  if (entity.kind !== "episode" || typeof entity.payload !== "string") {
    throw new Error("Invalid TeachingEpisodes row: expected episode payload");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(entity.payload);
  } catch {
    throw new Error("Invalid TeachingEpisodes row: payload is not JSON");
  }
  if (!object(parsed)) throw new Error("Invalid TeachingEpisodes row: payload is not an object");
  if (
    parsed.schemaVersion !== 1
    || typeof parsed.surfaceId !== "string"
    || !SURFACE_ID.test(parsed.surfaceId)
    || typeof parsed.episodeId !== "string"
    || !EPISODE_ID.test(parsed.episodeId)
    || typeof parsed.state !== "string"
    || !EPISODE_STATES.has(parsed.state)
    || parsed.surfaceId !== entity.partitionKey
    || parsed.episodeId !== entity.rowKey
    || parsed.state !== entity.state
    || !Array.isArray(parsed.spans)
    || parsed.spans.length === 0
  ) {
    throw new Error("Invalid TeachingEpisodes row: identity, state, or spans are malformed");
  }
  const spans = parsed.spans.map(parseSpan);
  if (entity.sourceConversationIds !== undefined) {
    let indexed: unknown;
    try {
      indexed = typeof entity.sourceConversationIds === "string"
        ? JSON.parse(entity.sourceConversationIds)
        : undefined;
    } catch {
      indexed = undefined;
    }
    const payloadIds = [...new Set(spans.map((span) => span.conversationId))];
    if (
      !Array.isArray(indexed)
      || indexed.length === 0
      || indexed.some((value) => typeof value !== "string" || !CONVERSATION_ID.test(value))
      || JSON.stringify(indexed) !== JSON.stringify(payloadIds)
    ) {
      throw new Error("Invalid TeachingEpisodes row: source conversation index does not match payload");
    }
  }
  const emptyIndex = spans.findIndex((span) => span.empty === true);
  if (
    emptyIndex >= 0
    && (emptyIndex !== spans.length - 1 || (parsed.state !== "cancelled" && parsed.state !== "expired"))
  ) {
    throw new Error("Invalid TeachingEpisodes row: empty span is not a terminal tombstone");
  }
  return { spans };
}

function claimsConversation(entity: TeachingEpisodeTableEntity, conversationId: string): boolean {
  if (typeof entity.sourceConversationIds === "string") {
    try {
      const indexed = JSON.parse(entity.sourceConversationIds) as unknown;
      if (Array.isArray(indexed) && indexed.includes(conversationId)) return true;
    } catch {
      // A parseable payload below can still attribute this row narrowly.
    }
  }
  if (typeof entity.payload !== "string") return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(entity.payload);
  } catch {
    return false;
  }
  if (!object(parsed) || !Array.isArray(parsed.spans)) return false;
  return parsed.spans.some((span) => object(span) && span.conversationId === conversationId);
}

function isInfrastructureRow(entity: TeachingEpisodeTableEntity): boolean {
  if (entity.kind === "open_guard") {
    return entity.rowKey === "__open__"
      && SURFACE_ID.test(entity.partitionKey)
      && typeof entity.episodeId === "string"
      && EPISODE_ID.test(entity.episodeId);
  }
  if (entity.kind === "proposal_job") {
    return SURFACE_ID.test(entity.partitionKey)
      && entity.rowKey.startsWith("job:")
      && EPISODE_ID.test(entity.rowKey.slice("job:".length));
  }
  return false;
}

function uniqueItemIndex(items: NormalizedItem[]): Map<string, number> {
  const positions = new Map<string, number>();
  for (const [index, item] of items.entries()) {
    if (typeof item.id !== "string" || !MESSAGE_ID.test(item.id) || positions.has(item.id)) {
      throw new Error("Teaching exclusion requires unique stable message ids");
    }
    positions.set(item.id, index);
  }
  return positions;
}

function addSpanItems(
  owned: Set<string>,
  span: TeachingSpanRecord,
  items: NormalizedItem[],
  positions: Map<string, number>,
): void {
  if (span.empty === true) return;
  let start = 0;
  if (span.startAfterItemId !== undefined) {
    const boundary = positions.get(span.startAfterItemId);
    if (boundary === undefined) throw new Error("Teaching exclusion start boundary is absent");
    start = boundary + 1;
  }
  let end = items.length - 1;
  if (span.endAtItemId !== undefined) {
    const boundary = positions.get(span.endAtItemId);
    if (boundary === undefined) throw new Error("Teaching exclusion end boundary is absent");
    end = boundary;
  }
  if (start > items.length || (span.endAtItemId !== undefined && start > end)) {
    throw new Error("Teaching exclusion span boundary is absent or out of order");
  }
  for (let index = start; index <= end; index += 1) {
    const id = items[index]?.id;
    if (!id) throw new Error("Teaching exclusion span resolved to an id-less item");
    owned.add(id);
  }
}

export function createTeachingExclusionSource(
  table: TeachingEpisodeTableSource,
  transcriptSnapshot: (conversationId: string) => NormalizedItem[] | undefined,
): TeachingEpisodeSource {
  return {
    async coverage(conversationId) {
      if (!CONVERSATION_ID.test(conversationId)) {
        throw new Error("Teaching exclusion requires a stable conversation id");
      }
      const matchingSpans: TeachingSpanRecord[] = [];
      let yielded = false;
      try {
        // Coverage follows a fresh transcript fetch. Deliberately re-scan the
        // complete durable ledger per conversation: this bounds the stale-read
        // race to one scan instead of a whole dream run. The extra Table cost is
        // the privacy-first tradeoff until the ledger has a safe queryable index.
        for await (const entity of table.listEntities()) {
          yielded = true;
          if (isInfrastructureRow(entity)) continue;
          if (!claimsConversation(entity, conversationId)) continue;
          const episode = parseEpisode(entity);
          matchingSpans.push(...episode.spans.filter((span) => span.conversationId === conversationId));
        }
      } catch (error) {
        const status = (error as { statusCode?: unknown } | null)?.statusCode;
        const code = (error as { code?: unknown } | null)?.code;
        // A missing table is authoritative only before enumeration begins.
        // Losing it between continuation pages would otherwise accept partial
        // coverage and permanently under-exclude the conversation.
        if (yielded || (status !== 404 && code !== "TableNotFound")) throw error;
      }
      if (matchingSpans.length === 0) return { hasTeachingEpisodes: false, teachingItemIds: [] };
      if (matchingSpans.every((span) => span.empty === true)) {
        return { hasTeachingEpisodes: true, teachingItemIds: [] };
      }
      const items = transcriptSnapshot(conversationId);
      if (!items) throw new Error("Teaching transcript snapshot is unavailable");
      const positions = uniqueItemIndex(items);
      const owned = new Set<string>();
      for (const span of matchingSpans) addSpanItems(owned, span, items, positions);
      return {
        hasTeachingEpisodes: true,
        teachingItemIds: items.flatMap((item) => item.id && owned.has(item.id) ? [item.id] : []),
      };
    },
  };
}
